import { useEffect } from 'react';
import LineItemTag, { autoDetectTag } from './LineItemTag';

const INPUT_STYLE = {
  width: '100%',
  border: 'none',
  background: 'transparent',
  fontSize: '13px',
  color: '#2b2b2b',
  fontFamily: 'inherit',
  padding: '6px 8px',
  outline: 'none',
  borderRadius: '4px',
  boxSizing: 'border-box',
};

function Cell({ children, style }) {
  return (
    <td style={{
      padding: '0',
      borderRight: '1px solid rgba(139,69,19,0.07)',
      verticalAlign: 'middle',
      ...style,
    }}>
      {children}
    </td>
  );
}

export default function LineItemRow({ row, index, onChange, onRemove, onDuplicate }) {
  useEffect(() => {
    if (!row.tagManual) {
      const detected = autoDetectTag(row.description);
      if (detected !== row.tag) onChange(row.id, 'tag', detected);
    }
  }, [row.description]);

  function update(field, value) { onChange(row.id, field, value); }

  function handleTagChange(key) {
    onChange(row.id, 'tag', key);
    onChange(row.id, 'tagManual', true);
  }

  const qty   = parseFloat(row.qty)   || 0;
  const price = parseFloat(row.price) || 0;
  const total = qty * price;

  // Alternating row backgrounds
  const rowBg = index % 2 === 0 ? '#ffffff' : '#fdfcfb';

  return (
    <tr style={{
      background: rowBg,
      borderBottom: '1px solid rgba(139,69,19,0.06)',
      // Taller rows — min height via lineHeight trick on cells
    }}>

      {/* # */}
      <Cell style={{ width: '36px', textAlign: 'center' }}>
        <span style={{ fontSize: '11px', color: '#ccc', fontWeight: '700', display: 'block', padding: '14px 0' }}>
          {index + 1}
        </span>
      </Cell>

      {/* Type tag — wider cell, tag fully visible */}
      <Cell style={{ width: '130px', padding: '10px 8px', textAlign: 'center' }}>
        <LineItemTag value={row.tag} onChange={handleTagChange} />
      </Cell>

      {/* Qty */}
      <Cell style={{ width: '56px' }}>
        <input
          type="number"
          value={row.qty}
          min="0"
          placeholder="1"
          onChange={e => update('qty', e.target.value)}
          style={{ ...INPUT_STYLE, textAlign: 'center' }}
        />
      </Cell>

      {/* Description */}
      <Cell style={{ minWidth: '180px' }}>
        <input
          type="text"
          value={row.description}
          placeholder="Description..."
          onChange={e => update('description', e.target.value)}
          style={{ ...INPUT_STYLE }}
        />
      </Cell>

      {/* Room */}
      <Cell style={{ width: '115px' }}>
        <input
          type="text"
          value={row.room}
          placeholder="Room..."
          onChange={e => update('room', e.target.value)}
          style={{ ...INPUT_STYLE }}
        />
      </Cell>

      {/* Size */}
      <Cell style={{ width: '105px' }}>
        <input
          type="text"
          value={row.size}
          placeholder='23 1/2 x 49"'
          onChange={e => update('size', e.target.value)}
          style={{ ...INPUT_STYLE, fontFamily: 'monospace', fontSize: '12px' }}
        />
      </Cell>

      {/* UI */}
      <Cell style={{ width: '80px' }}>
        <input
          type="text"
          value={row.ui}
          placeholder="UI..."
          onChange={e => update('ui', e.target.value)}
          style={{ ...INPUT_STYLE }}
        />
      </Cell>

      {/* Unit Price */}
      <Cell style={{ width: '95px' }}>
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
          <span style={{ fontSize: '12px', color: '#aaa', marginRight: '2px', flexShrink: 0 }}>$</span>
          <input
            type="number"
            value={row.price}
            min="0"
            step="0.01"
            placeholder="0.00"
            onChange={e => update('price', e.target.value)}
            style={{ ...INPUT_STYLE, paddingLeft: '2px' }}
          />
        </div>
      </Cell>

      {/* Total */}
      <Cell style={{ width: '95px', textAlign: 'right', paddingRight: '12px', borderRight: 'none' }}>
        <span style={{
          fontSize: '13px',
          fontWeight: '700',
          color: total > 0 ? '#2b2b2b' : '#ddd',
          fontFamily: 'monospace',
        }}>
          ${total.toFixed(2)}
        </span>
      </Cell>

      {/* Actions */}
      <Cell style={{ width: '64px', textAlign: 'center', borderRight: 'none' }}>
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', padding: '0 4px' }}>
          <button
            type="button"
            onClick={() => onDuplicate(row.id)}
            title="Duplicate row"
            style={{
              width: '26px', height: '26px', borderRadius: '6px',
              border: '1px solid rgba(139,69,19,0.15)',
              background: 'transparent', cursor: 'pointer',
              fontSize: '13px', color: '#8B4513',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,69,19,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            ⧉
          </button>
          <button
            type="button"
            onClick={() => onRemove(row.id)}
            title="Remove row"
            style={{
              width: '26px', height: '26px', borderRadius: '6px',
              border: '1px solid rgba(220,50,50,0.2)',
              background: 'transparent', cursor: 'pointer',
              fontSize: '15px', color: '#cc3333',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,50,50,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            ×
          </button>
        </div>
      </Cell>
    </tr>
  );
}